// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import {
  SearchHistory,
  SearchHistoryItem,
} from '../../search-history-service/search-history.service';

/**
 * Item with a record that indicates WHAT was matched by a search string
 */
export type SearchHistoryItemHighlighted = SearchHistoryItem & {
  _key: number; // important to reidentify items downstream;
  highlights: Record<keyof SearchHistoryItem, { start: number; end: number }[]>;
  unsupported?: boolean;
};

export type SearchHistoryHighlighted = SearchHistoryItemHighlighted[];

/**
 * matches a system with a search string
 */
export function filterItems(
  items: SearchHistory,
  searchString: string,
): SearchHistoryHighlighted {
  const searchPattern = new RegExp(searchString, 'i');
  const matched: SearchHistoryHighlighted = [];
  items.forEach((item) => {
    const matches = [
      {
        key: 'customerName' as keyof SearchHistoryItem,
        match: matchString(item.customerName, searchString, searchPattern),
      },
      {
        key: 'customerLocation' as keyof SearchHistoryItem,
        match: matchString(item.customerLocation, searchString, searchPattern),
      },
      {
        key: 'systemSerialNumber' as keyof SearchHistoryItem,
        match: matchString(
          item.systemSerialNumber,
          searchString,
          searchPattern,
        ),
      },
      {
        key: 'functionalLocation' as keyof SearchHistoryItem,
        match: matchString(
          item.functionalLocation ?? '',
          searchString,
          searchPattern,
        ),
      },
      {
        key: 'systemMaterialNumber' as keyof SearchHistoryItem,
        match: matchString(
          item.systemMaterialNumber.toString(),
          searchString,
          searchPattern,
        ),
      },
      {
        key: 'systemName' as keyof SearchHistoryItem,
        match: matchString(item.systemName, searchString, searchPattern),
      },
      {
        key: 'equipmentTechId' as keyof SearchHistoryItem,
        match: matchString(
          item.equipmentTechId ?? '',
          searchString,
          searchPattern,
        ),
      },
      {
        key: 'equipmentId' as keyof SearchHistoryItem,
        match: matchString(item.equipmentId ?? '', searchString, searchPattern),
      },
    ].filter(({ match }) => !!match);

    if (matches.some((m) => !!m)) {
      const highlights = matches.reduce(
        (acc, { key, match }) => ({
          ...acc,
          ...(match?.index !== undefined
            ? {
                [key as keyof SearchHistoryItem]: [
                  {
                    start: match.index,
                    end: match.index + searchString.length,
                  },
                ],
              }
            : {}),
        }),
        {} as SearchHistoryItemHighlighted['highlights'],
      );

      matched.push({
        ...keyItem(item),
        highlights,
      });
    }
  });

  return matched;
}

/**
 * matches a string with a search string
 */
export function matchString(
  target: string,
  searchString: string,
  r = new RegExp(searchString, 'i'),
): RegExpMatchArray | null {
  return target.match(r);
}

let itemKey = 0;
const keys = new Map<string, number>();
export function keyItem(
  item: Partial<SearchHistoryItem | SearchHistoryItemHighlighted>,
): SearchHistoryItemHighlighted {
  if (!('_key' in (item as SearchHistoryItemHighlighted))) {
    const k = item.systemSerialNumber! + item.systemMaterialNumber!;
    const key = keys.get(k) || itemKey++;
    keys.set(k, key);
    (item as SearchHistoryItemHighlighted)._key = key;
  }
  return item as SearchHistoryItemHighlighted;
}
